// src/Pages/Tietosuoja/Tietosuoja.jsx
import React from "react";
import { Helmet } from "@dr.pogodin/react-helmet";
import { motion } from "../../lib/motionSafe"; // polku suhteessa komponenttiin
import { useTheme } from "../../context/ThemeContext";
import VideoHero from "../../components/VideoHero/VideoHero.jsx";
import heroMp4 from "../../assets/ukk.mp4";
import poster  from "../../assets/ukk-hero-poster.webp";

export default function Tietosuoja() {
  const { theme } = useTheme();

  // Hero overlay – hieman tummempi vaaleassa teemassa
  const heroOverlay =
    theme === "dark"
      ? "from-black/60 via-black/35 to-black/60"
      : "from-black/40 via-black/60 to-black/75";

  // *** SISÄLLÖN (leipäteksti + väliotsikot) värit ***
  const contentBodyText  = theme === "dark" ? "text-white/80" : "text-black/80";
  const contentTitleText = theme === "dark" ? "text-white/90" : "text-black/90";

  // *** HERON värit ERILLÄÄN sisällöstä ***
  // Voit vaihtaa näitä vaikuttamatta leipätekstin väriin.
  const heroTitleText = "text-electric";      // esim. "text-electric" jos haluat
  const heroLeadText  = "text-electric/90 font-semibold";   // esim. "text-white/80"

  // Fonttikoot/perustyylit
  const hFont = "font-customFont2 text-lg xxxsm:text-sm xxsm:text-sm s24:text-sm xsm:text-sm sm:text-lg md:text-lg mdd:text-lg font-semibold";
  const pFont = "font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm";

  // Kiinteä viimeisin päivityspäivä: hallitse .env:llä tai päivitä käsin kun sisältö muuttuu
  const POLICY_LAST_UPDATED = process.env.REACT_APP_POLICY_LAST_UPDATED || "2.11.2025";

  return (
    <>
      <Helmet>
        <title>Tietosuojaseloste – Buukkeri</title>
        {/*
          Meta-kuvaukset, OG/Twitter hoidetaan palvelimelta (BBX SEO CORE / React Shell).
          Pidetään clientissä vain <title> välttääksemme duplikaatiot.
        */}
      </Helmet>

      {/* HERO – käyttää vain hero* -värejä */}
      <VideoHero
        mp4Src={heroMp4}
        poster={poster}
        className="h-[32vh] min-h-[260px] mt-20"
        overlayClass={`bg-gradient-to-b ${heroOverlay}`}
      >
        <motion.h1
          className={`text-center ${hFont} ${heroTitleText} text-4xl sm:text-5xl lg:text-6xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.65)]`}
          initial={{ y: 12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          Tietosuojaseloste
        </motion.h1>
        <motion.p
          className={`mt-3 text-center ${pFont} ${heroLeadText} max-w-3xl mx-auto drop-shadow-[0_1px_6px_rgba(0,0,0,0.55)]`}
          initial={{ y: 12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, ease: "easeOut", delay: 0.05 }}
        >
          Voimassa 1.1.2025 alkaen — viimeksi päivitetty {POLICY_LAST_UPDATED}
        </motion.p>
      </VideoHero>

      {/* SISÄLTÖ – käyttää content* -värejä */}
      <main className="relative w-full pb-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl p-6 bg-lemon/60 dark:bg-black/40 backdrop-blur-md shadow-lg ring-1 ring-white/10">

            <Section title="1. Rekisterinpitäjä" titleClass={`${hFont} ${contentTitleText}`} bodyClass={`${pFont} ${contentBodyText}`}>
              <address className="not-italic">
                Buukkeri<br />
                Kerava, Uusimaa<br />
                Sähköposti: <a href="mailto:info@buukkeri.fi">info@buukkeri.fi</a><br />
                Puhelin: <a href="tel:+358443519265">044&nbsp;351&nbsp;9265</a>
              </address>
            </Section>

            <Section title="2. Henkilötietojen käsittelyn peruste ja tarkoitukset" titleClass={`${hFont} ${contentTitleText}`} bodyClass={`${pFont} ${contentBodyText}`}>
              <ul className="list-disc list-inside space-y-1 marker:text-current">
                <li><strong>Sopimus</strong>: tilin luominen, profiilin hallinta, varausten ja sopimusten hoitaminen (tilaaja ↔ esiintyjä), tarjousten ja vahvistusten lähetys sähköpostitse.</li>
                <li><strong>Oikeutettu etu</strong>: palvelun kehitys, tietoturva ja väärinkäytösten estäminen, asiakaspalvelu, näkyvyyden ja hakutulosten toiminta.</li>
                <li><strong>Suostumus</strong>: markkinointiviestit sekä analytiikka- ja markkinointievästeet.</li>
                <li><strong>Palautteet</strong>: automaattinen palautepyyntö keikan jälkeen PIN-koodilla (arvostelun antaminen ja näyttäminen palvelussa).</li>
              </ul>
            </Section>

            <Section title="3. Käsiteltävät henkilötiedot" titleClass={`${hFont} ${contentTitleText}`} bodyClass={`${pFont} ${contentBodyText}`}>
              <ul className="list-disc list-inside space-y-1 marker:text-current">
                <li>Perustiedot: nimi/esiintymisnimi, sähköposti, salasana.</li>
                <li>Profiilitiedot: kuvaus, kuvat, videot ja äänet, paikkakunta, kalenteri, hinnat ja keikkailualueet.</li>
                <li>Varaus- ja sopimustiedot: keikkapäivä ja -paikka (osoite), yhteyshenkilö ja puhelin, setit, matkakulut, tarjouksen ja hyväksynnän aikaleimat ja tunnistetiedot.</li>
                <li>Viestit ja lokit: palvelun sisäinen viestintä, kirjautumis- ja tapahtumalokit (kuten IP-osoite ja laitteen perustiedot).</li>
                <li>Arvostelut: tähdet ja vapaamuotoinen palaute (PIN-koodilla avattava lomake).</li>
                <li>Laskutus- ja tilitystiedot <em>vain jos</em> hyödynnät Buukkerin apua laskutuksessa (esim. maksajan laskutustiedot).</li>
                <li>Eväste- ja analytiikkatiedot suostumuksen perusteella.</li>
              </ul>
            </Section>

            <Section title="4. Tietolähteet" titleClass={`${hFont} ${contentTitleText}`} bodyClass={`${pFont} ${contentBodyText}`}>
              <p>Tiedot saadaan käyttäjältä itseltään (esiintyjä/tilaaja) palvelun käytön yhteydessä, tilausten ja viestinnän kautta sekä teknisistä lokijärjestelmistä ja evästeistä.</p>
            </Section>

            <Section title="5. Säilytysajat" titleClass={`${hFont} ${contentTitleText}`} bodyClass={`${pFont} ${contentBodyText}`}>
              <ul className="list-disc list-inside space-y-1 marker:text-current">
                <li>Tili- ja sopimustiedot: niin kauan kuin tili on aktiivinen ja kohtuullinen aika sen jälkeen (esim. 24 kk) oikeusvaateiden käsittelemiseksi.</li>
                <li>Kirjanpitoon liittyvät tiedot lain edellyttämän ajan.</li>
                <li>Lokit ja analytiikka: tyypillisesti 12–24 kk.</li>
              </ul>
            </Section>

            <Section title="6. Tietojen luovutukset ja vastaanottajat" titleClass={`${hFont} ${contentTitleText}`} bodyClass={`${pFont} ${contentBodyText}`}>
              <p>
                Tietoja voidaan käsitellä Buukkerin käyttämien palveluntarjoajien (esim. hosting, sähköpostin lähetys, analytiikka, maksunvälitys/laskutusapu)
                järjestelmissä. Varausten toteuttamiseksi <strong>tilaaja ja esiintyjä</strong> saavat toisistaan tarpeelliset yhteys- ja varaustiedot.
                Tietoja voidaan luovuttaa viranomaisille lain velvoittaessa.
              </p>
            </Section>

            <Section title="7. Siirrot EU/ETA-alueen ulkopuolelle" titleClass={`${hFont} ${contentTitleText}`} bodyClass={`${pFont} ${contentBodyText}`}>
              <p>
                Palvelininfrastruktuuri sijaitsee <b>Yhdysvalloissa (New York)</b>. Mikäli tietoja siirretään EU/ETA-alueen ulkopuolelle,
                käytämme EU:n mallisopimuslausekkeita (SCC) tai muuta tietosuojalainsäädännön mukaista suojamekanismia sekä soveltuvin osin
                lisäsuojia (kuten salaus kuljetuksessa ja levossa).
              </p>
            </Section>

            <Section title="8. Rekisteröidyn oikeudet" titleClass={`${hFont} ${contentTitleText}`} bodyClass={`${pFont} ${contentBodyText}`}>
              <ul className="list-disc list-inside space-y-1 marker:text-current">
                <li>Oikeus tarkastaa omat tiedot.</li>
                <li>Oikeus oikaista virheelliset tiedot ja täydentää puutteellisia.</li>
                <li>Oikeus poistaa tiedot (“tulla unohdetuksi”) lain sallimissa rajoissa.</li>
                <li>Oikeus rajoittaa ja vastustaa käsittelyä sekä oikeus siirtää tiedot järjestelmästä toiseen.</li>
                <li>Oikeus peruuttaa suostumus (esim. markkinointi, evästeet) milloin tahansa.</li>
                <li>Oikeus tehdä valitus valvontaviranomaiselle (Tietosuojavaltuutetun toimisto).</li>
              </ul>
            </Section>

            <Section title="9. Evästeet" titleClass={`${hFont} ${contentTitleText}`} bodyClass={`${pFont} ${contentBodyText}`}>
              <p>
                Käytämme välttämättömiä evästeitä palvelun toiminnallisuuteen sekä suostumukseen perustuvia analytiikka- ja
                markkinointievästeitä. Voit hallita valintojasi sivun alareunan evästeasetuksista. Välttämättömiä evästeitä
                ei voi estää, koska ne ovat edellytys palvelun käytölle.
              </p>
            </Section>

            <Section title="10. Tietoturva" titleClass={`${hFont} ${contentTitleText}`} bodyClass={`${pFont} ${contentBodyText}`}>
              <p>Suojamme tiedot mm. käyttöoikeusrajauksin, salauksin ja valvonnalla. Pääsy tietoihin on rajattu vain niille, joilla on työtehtäviensä perusteella tarve.</p>
            </Section>

            <Section title="11. Muutokset selosteeseen" titleClass={`${hFont} ${contentTitleText}`} bodyClass={`${pFont} ${contentBodyText}`}>
              <p>Voimme päivittää tätä selostetta. Merkittävistä muutoksista ilmoitetaan palvelussa. Jatkamalla käyttöä hyväksyt muutokset.</p>
            </Section>

            <Section title="12. Yhteydenotot" titleClass={`${hFont} ${contentTitleText}`} bodyClass={`${pFont} ${contentBodyText}`}>
              <p>Kaikissa tietosuojaa koskevissa asioissa ota yhteyttä: <a href="mailto:info@buukkeri.fi">info@buukkeri.fi</a>.</p>
            </Section>

          </div>
        </div>
      </main>
    </>
  );
}

function Section({ title, children, titleClass = "", bodyClass = "" }) {
  return (
    <section className="mt-6 first:mt-0">
      <h2 className={`mb-2 ${titleClass}`}>{title}</h2>
      {/* Yhtenäiset lapsityylit vain sisältöön */}
      <div
        className={[
          "leading-relaxed",
          bodyClass,
          "[&_a]:text-inherit",
          "[&_a]:underline",
          "[&_a]:decoration-2",
          "[&_a]:underline-offset-2",
          "dark:[&_a]:decoration-white/30",
          "[&_a]:decoration-black/30",
          "[&_a:hover]:decoration-electric",
          "[&_a:focus-visible]:outline-none",
          "[&_a:focus-visible]:ring-2",
          "[&_a:focus-visible]:ring-electric/60",
          "[&_ul]:list-disc",
          "[&_ul]:list-inside",
          "[&_ul]:marker:text-current",
        ].join(" ")}
      >
        {children}
      </div>
    </section>
  );
}
